<template>
  <div class="flash-card" :class="[cardClass, { 'is-active': active }]">
    <template v-if="!playing">
      <template v-if="isGallery">
        <div
          class="card-bg-image"
          :style="{ backgroundImage: `url(${currentGalleryImage})` }"
        ></div>
        <div class="gallery-tap gallery-tap-left" @click.stop="prevImage"></div>
        <div class="gallery-tap gallery-tap-right" @click.stop="nextImage"></div>
        <div class="gallery-dots">
          <span
            v-for="(_, i) in galleryImages.length"
            :key="i"
            :class="['gdot', { active: i === currentImage }]"
            @click.stop="currentImage = i"
          ></span>
        </div>
      </template>
      <div
        v-else-if="displayBg"
        class="card-bg-image"
        :style="{ backgroundImage: `url(${displayBg})` }"
      ></div>
      <div v-if="isPlayableMedia" class="card-video-overlay" @click="togglePlay">
        <div class="play-icon">
          <b-icon icon="play-circle-fill" font-scale="3.5"></b-icon>
        </div>
      </div>
      <div class="card-gradient-overlay"></div>
      <div class="card-content">
        <div class="card-top">
          <b-badge v-if="hat" variant="danger" class="hat-badge">{{ hat }}</b-badge>
          <b-badge v-if="item.exclusive" variant="warning" class="exclusive-badge">EXCLUSIVO</b-badge>
        </div>
        <div class="card-bottom">
          <h2 class="card-title">{{ title }}</h2>
          <p v-if="showTextFallback" class="card-body-text">{{ bodyText }}</p>
          <p v-else-if="abstract" class="card-abstract">{{ abstract }}</p>
          <div class="card-meta">
            <span class="card-date">{{ formattedDate }}</span>
            <span class="card-subject">{{ subjectName }}</span>
            <span v-if="mediaKindLabel" class="card-duration">
              <b-icon :icon="mediaKindIcon" font-scale="0.8"></b-icon> {{ mediaKindLabel }}
            </span>
          </div>
          <p class="card-author">{{ authorName }}</p>
        </div>
      </div>
    </template>
    <div v-else class="card-player" @click.stop>
      <iframe
        v-if="mediaKind === 'youtube' || mediaKind === 'podcast'"
        :src="embedUrl"
        frameborder="0"
        allow="autoplay; encrypted-media; fullscreen"
        allowfullscreen
      ></iframe>
      <video
        v-else-if="mediaKind === 'video'"
        :src="item.video.image_path"
        controls
        autoplay
        playsinline
      ></video>
      <div v-else-if="mediaKind === 'audio'" class="audio-player">
        <audio :src="item.audio.image_path" controls autoplay></audio>
      </div>
      <button class="player-close" @click.stop="playing = false" title="Fechar">
        <b-icon icon="x-lg"></b-icon>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    item: { type: Object, required: true },
    active: { type: Boolean, default: false },
  },
  data() {
    return {
      playing: false,
      currentImage: 0,
      touchStartX: 0,
    }
  },
  computed: {
    title() {
      return this.item.content?.title?.data || ''
    },
    abstract() {
      return this.item.content?.abstract?.data || ''
    },
    bodyText() {
      const html = this.item.content?.body?.data || ''
      return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    },
    imageUrl() {
      return this.item.image || this.item.thumb?.image_path || null
    },
    youtubeThumb() {
      if (!this.item.youtubeId) return null
      return `https://img.youtube.com/vi/${this.item.youtubeId}/hqdefault.jpg`
    },
    galleryImages() {
      return this.item.gallery || null
    },
    isGallery() {
      return Array.isArray(this.galleryImages) && this.galleryImages.length > 1
    },
    currentGalleryImage() {
      if (!this.isGallery) return null
      return this.galleryImages[this.currentImage]
    },
    displayBg() {
      return this.imageUrl || this.youtubeThumb || null
    },
    mediaKind() {
      if (this.item.youtubeId) return 'youtube'
      if (this.item.video?.image_path) return 'video'
      if (this.item.soundcloud || this.item.spotify) return 'podcast'
      if (this.item.audio?.image_path) return 'audio'
      return 'none'
    },
    isPlayableMedia() {
      return this.mediaKind !== 'none'
    },
    showTextFallback() {
      return this.mediaKind === 'none' && !this.displayBg && !!this.bodyText
    },
    mediaKindLabel() {
      return { youtube: 'Vídeo', video: 'Vídeo', podcast: 'Podcast', audio: 'Áudio' }[this.mediaKind] || ''
    },
    mediaKindIcon() {
      return { youtube: 'play-btn', video: 'play-btn', podcast: 'mic-fill', audio: 'volume-up-fill' }[this.mediaKind] || 'play-btn'
    },
    hat() {
      return this.item.hat?.length ? this.item.hat[0].name : null
    },
    subjectName() {
      return this.item.subjects?.length ? this.item.subjects[0].name : ''
    },
    authorName() {
      return this.item.author?.name || ''
    },
    formattedDate() {
      if (!this.item.dt_publish) return ''
      const d = new Date(this.item.dt_publish)
      const opts = { day: '2-digit', month: 'short' }
      return d.toLocaleDateString('pt-BR', opts)
    },
    cardClass() {
      if (this.playing) return 'card-playing'
      if (this.isGallery || this.displayBg) return 'card-image'
      return 'card-text'
    },
    embedUrl() {
      if (this.mediaKind === 'youtube') {
        return `https://www.youtube.com/embed/${this.item.youtubeId}?autoplay=1&rel=0`
      }
      if (this.mediaKind === 'podcast') {
        if (this.item.spotify) {
          return this.item.spotify.replace('open.spotify.com/', 'open.spotify.com/embed/')
        }
        if (this.item.soundcloud) {
          return `https://w.soundcloud.com/player/?url=${encodeURIComponent(this.item.soundcloud)}&auto_play=true&color=%23ff5500`
        }
      }
      return ''
    },
  },
  watch: {
    active(isActive) {
      if (!isActive) this.playing = false
    },
  },
  mounted() {
    if (!this.$el) return
    this.$el.addEventListener('touchstart', this.onTouchStart, { passive: true })
    this.$el.addEventListener('touchend', this.onTouchEnd, { passive: true })
  },
  beforeDestroy() {
    if (this.$el) {
      this.$el.removeEventListener('touchstart', this.onTouchStart)
      this.$el.removeEventListener('touchend', this.onTouchEnd)
    }
  },
  methods: {
    togglePlay() {
      if (this.isPlayableMedia) {
        this.playing = true
      }
    },
    prevImage() {
      if (!this.isGallery) return
      this.currentImage = this.currentImage === 0
        ? this.galleryImages.length - 1
        : this.currentImage - 1
    },
    nextImage() {
      if (!this.isGallery) return
      this.currentImage = this.currentImage === this.galleryImages.length - 1
        ? 0
        : this.currentImage + 1
    },
    onTouchStart(e) {
      this.touchStartX = e.touches[0].clientX
    },
    onTouchEnd(e) {
      if (!this.isGallery || this.touchStartX === 0) return
      const deltaX = e.changedTouches[0].clientX - this.touchStartX
      if (Math.abs(deltaX) > 40) {
        if (deltaX > 0) this.prevImage()
        else this.nextImage()
      }
      this.touchStartX = 0
    },
  },
}
</script>

<style scoped>
.flash-card {
  position: relative;
  width: 100%;
  height: 100%;
  scroll-snap-align: start;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
}
.card-bg-image {
  position: absolute;
  inset: 0;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  transform: scale(1);
}
.flash-card.is-active .card-bg-image {
  animation: flashKenBurns 6s cubic-bezier(0.22, 1, 0.36, 1) both;
}
.card-video-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}
.play-icon {
  color: rgba(255,255,255,0.85);
  text-shadow: 0 2px 8px rgba(0,0,0,0.4);
  animation: flashPulse 2.2s ease-in-out infinite;
}
.card-gradient-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0.1) 70%, rgba(0,0,0,0) 100%);
  z-index: 1;
}
.card-content {
  position: relative;
  z-index: 2;
  width: 100%;
  padding: 24px 20px 48px;
}
.card-top {
  margin-bottom: 12px;
  display: flex;
  gap: 8px;
  opacity: 0;
}
.flash-card.is-active .card-top {
  animation: flashFadeUp 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: 0.05s;
}
.hat-badge {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 4px 10px;
  border-radius: 4px;
  text-transform: uppercase;
}
.exclusive-badge {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 4px 10px;
  border-radius: 4px;
}
.card-title {
  color: #fff;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 8px;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
  opacity: 0;
}
.flash-card.is-active .card-title {
  animation: flashFadeUp 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: 0.12s;
}
.card-abstract {
  color: rgba(255,255,255,0.8);
  font-size: 14px;
  line-height: 1.4;
  margin: 0 0 12px;
  opacity: 0;
}
.flash-card.is-active .card-abstract {
  animation: flashFadeUp 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: 0.18s;
}
.card-body-text {
  color: rgba(255,255,255,0.85);
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 12px;
  display: -webkit-box;
  -webkit-line-clamp: 8;
  -webkit-box-orient: vertical;
  overflow: hidden;
  opacity: 0;
}
.flash-card.is-active .card-body-text {
  animation: flashFadeUp 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: 0.18s;
}
.card-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  opacity: 0;
}
.flash-card.is-active .card-meta {
  animation: flashFadeUp 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: 0.24s;
}
.card-date {
  color: rgba(255,255,255,0.7);
  font-size: 12px;
}
.card-subject {
  color: rgba(255,255,255,0.6);
  font-size: 12px;
  background: rgba(255,255,255,0.1);
  padding: 2px 10px;
  border-radius: 100px;
}
.card-duration {
  color: rgba(255,255,255,0.7);
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.card-author {
  color: rgba(255,255,255,0.5);
  font-size: 11px;
  margin: 6px 0 0;
  opacity: 0;
}
.flash-card.is-active .card-author {
  animation: flashFadeUp 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: 0.3s;
}

@keyframes flashFadeUp {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes flashKenBurns {
  from {
    transform: scale(1.08);
  }
  to {
    transform: scale(1);
  }
}

@keyframes flashPulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.85;
  }
  50% {
    transform: scale(1.08);
    opacity: 1;
  }
}

.card-player {
  position: absolute;
  inset: 0;
  z-index: 5;
  background: #000;
}
.card-player iframe,
.card-player video {
  width: 100%;
  height: 100%;
  border: 0;
}
.audio-player {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
}
.audio-player audio {
  width: 100%;
}
.player-close {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 10;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(0,0,0,0.5);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(4px);
}

.card-playing {
  cursor: default;
}

.gallery-tap {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 40%;
  z-index: 3;
  cursor: pointer;
}
.gallery-tap-left { left: 0; }
.gallery-tap-right { right: 0; }

.gallery-dots {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
  z-index: 4;
  pointer-events: auto;
}
.gdot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255,255,255,0.4);
  transition: all 0.2s;
  cursor: pointer;
}
.gdot.active {
  width: 20px;
  border-radius: 4px;
  background: #fff;
}

.card-text {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
}
.card-text .card-gradient-overlay {
  background: linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 60%);
}
</style>
